import { useDCSText } from 'hooks/useDCSText';
import React from 'react';

import { getField } from 'utils/EntryUtil';

import { generateId } from 'utils/IdGeneration';

import { testAttribute } from 'utils/DataTestId';
import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';
import type { IFieldWrapperProps } from './IFieldWrapperProps';

const HEADER_TAG = /^(h|H)([1-6])$/;

export function Text<T, E extends HTMLElement = HTMLSpanElement>({
  entry,
  field,
  innerRef,
  tagName,
  children,
  ...props
}: IFieldWrapperProps<T, E>) {
  if (!field) return null;

  const value = getField(entry, field) as string | undefined;

  if (!value) return null;

  const text = useDCSText(value);

  const Wrapped = React.useCallback(
    applyEntryDataAttributes<T>(({ entry, field, ...props }) =>
      React.createElement(tagName as string, props),
    ),
    [tagName],
  );

  return (
    <Wrapped
      data-testid={testAttribute(String(value))}
      id={HEADER_TAG.test(tagName as string) ? generateId(`${value}`) : undefined}
      ref={innerRef}
      entry={entry}
      field={field}
      {...props}
    >
      {/*
      Bringing the hyphenated-word to the next line instead of breaking it, by replacing hypen(-) with non-breakable hyphen.
      */}
      {String(text).replace(/-/g, '\u2011')}
      {children}
    </Wrapped>
  );
}

Text.defaultProps = { tagName: 'span' as keyof React.ReactHTML };
